/**
 * Grid Navigation Utilities
 *
 * Shared utilities for keyboard navigation between AG Grid instances
 * (PlanEditor, RatesGrid, SummaryGrid) in the BTX Build 2.0 workbench.
 *
 * IMPORTANT: This module uses module-level state for tab direction tracking.
 * This assumes only ONE set of aligned grids (PlanEditor + RatesGrid + SummaryGrid)
 * exists on the page at a time. If multiple independent grid sets need to coexist,
 * this state management would need to be refactored to use React Context or refs
 * scoped to each grid set.
 *
 * Grid identification uses explicit data attributes:
 * - data-grid-id="plan-editor" on PlanEditor wrapper
 * - data-grid-id="rates-grid" on RatesGrid wrapper
 */

// =============================================================================
// Grid Identification Constants
// =============================================================================

/** Data attribute used to identify grid containers */
export const GRID_ID_ATTR = "data-grid-id";

/** Grid ID values */
export const GRID_IDS = {
  PLAN_EDITOR: "plan-editor",
  RATES_GRID: "rates-grid",
} as const;

// =============================================================================
// AG Grid CSS Selectors
// =============================================================================

/** AG Grid root wrapper selector */
export const AG_ROOT_WRAPPER = ".ag-root-wrapper";

/** AG Grid body viewport selector */
export const AG_BODY_VIEWPORT = ".ag-body-viewport";

/** AG Grid cell selector */
export const AG_CELL = ".ag-cell";

/** AG Grid cell with aria-colindex (indicates editable grid like PlanEditor) */
export const AG_CELL_WITH_COLINDEX = ".ag-cell[aria-colindex]";

// =============================================================================
// Tab Direction Tracking
// =============================================================================

/**
 * Track the last Tab navigation direction for focus delegation.
 * When AG Grid moves focus to a new cell, FocusableRateCellWrapper reads this
 * to determine whether to focus the first input (forward) or last input (backward).
 *
 * Uses getter/setter pattern to avoid react-compiler side-effect errors.
 */
let _lastTabDirection: "forward" | "backward" = "forward";

export const getLastTabDirection = (): "forward" | "backward" =>
  _lastTabDirection;

export const setLastTabDirection = (dir: "forward" | "backward"): void => {
  _lastTabDirection = dir;
};

// =============================================================================
// Focus Navigation Functions
// =============================================================================

/**
 * Focus a grid cell by its navigation data attribute
 * @internal Exported for testing
 */
export const focusGridCell = (gridId: string, index: number): boolean => {
  const selector = `[data-grid-nav="${gridId}-${index}"]`;
  const element = document.querySelector<HTMLInputElement>(selector);
  if (element) {
    element.focus();
    element.select();
    return true;
  }
  return false;
};

/**
 * Focus the last editable cell in PlanEditor when Shift+Tab escapes RatesGrid
 * This mirrors the logic in PlanEditor that focuses RatesGrid's last input on Shift+Tab
 * @internal Exported for testing
 */
export const focusPlanEditorLastCell = (): boolean => {
  // Find PlanEditor by data attribute (explicit identification)
  const planEditorContainer = document.querySelector(
    `[${GRID_ID_ATTR}="${GRID_IDS.PLAN_EDITOR}"]`,
  );

  if (!planEditorContainer) {
    // Fallback: search all grids if data attribute not found
    const allGrids = Array.from(document.querySelectorAll(AG_ROOT_WRAPPER));

    for (const grid of allGrids) {
      // Skip if this is the RatesGrid (contains the currently focused element)
      if (grid.contains(document.activeElement)) continue;

      // Find the last editable cell in the grid body
      const bodyViewport = grid.querySelector(AG_BODY_VIEWPORT);
      if (!bodyViewport) continue;

      const allCells = bodyViewport.querySelectorAll(AG_CELL);
      if (allCells.length === 0) continue;

      const lastCell = allCells[allCells.length - 1] as HTMLElement;
      if (lastCell) {
        lastCell.setAttribute("tabindex", "0");
        lastCell.focus();
        return true;
      }
    }
    return false;
  }

  // Find the AG Grid wrapper inside the PlanEditor container
  const agGridWrapper = planEditorContainer.querySelector(AG_ROOT_WRAPPER);
  if (!agGridWrapper) return false;

  // Find the last editable cell in PlanEditor body
  const bodyViewport = agGridWrapper.querySelector(AG_BODY_VIEWPORT);
  if (!bodyViewport) return false;

  const allCells = bodyViewport.querySelectorAll(AG_CELL);
  if (allCells.length === 0) return false;

  // Get the last cell and focus it
  const lastCell = allCells[allCells.length - 1] as HTMLElement;
  if (lastCell) {
    lastCell.setAttribute("tabindex", "0");
    lastCell.focus();
    return true;
  }

  return false;
};

/**
 * Focus the last input in RatesGrid when Shift+Tab escapes PlanEditor
 * Uses data attribute for explicit grid identification
 * @internal Exported for testing
 */
export const focusRatesGridLastInput = (): boolean => {
  // Find RatesGrid by data attribute (explicit identification)
  const ratesGridContainer = document.querySelector(
    `[${GRID_ID_ATTR}="${GRID_IDS.RATES_GRID}"]`,
  );

  if (!ratesGridContainer) {
    // Fallback: search all grids if data attribute not found
    const allGrids = Array.from(document.querySelectorAll(AG_ROOT_WRAPPER));

    for (const grid of allGrids) {
      // Skip if this is the PlanEditor (contains the currently focused element)
      if (grid.contains(document.activeElement)) continue;

      const inputs = grid.querySelectorAll(`${AG_BODY_VIEWPORT} input`);
      if (inputs.length > 0) {
        const lastInput = inputs[inputs.length - 1] as HTMLInputElement;
        lastInput.focus();
        lastInput.select();
        return true;
      }
    }
    return false;
  }

  // Find all inputs in RatesGrid body
  const inputs = ratesGridContainer.querySelectorAll(
    `${AG_BODY_VIEWPORT} input`,
  );
  if (inputs.length === 0) return false;

  const lastInput = inputs[inputs.length - 1] as HTMLInputElement;
  lastInput.focus();
  lastInput.select();
  return true;
};
